import { enableProdMode, importProvidersFrom } from '@angular/core';

import { environment } from 'landlord-env';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { AppComponent } from 'landlord/app.component';
import { guards } from 'landlord/guards';
import {
  AdblockDetectorService,
  BodyService,
  ComponentsModule as SharedComponentsModule,
  ComponentsModuleConfig,
  CONSTANTS_LOADER,
  CustomSerializer,
  DateTimeService,
  EnvironmentConfig,
  ImmomioTheme,
  InfrastructureModule,
  KeycloakAuthenticationModule,
  NewRelicService,
  PipesModule,
  PortalsModule,
  ProductsModule,
  PropertiesModule,
  ThemeModule,
  ThemeUrls
} from '@ui/legacy-lib';
import { provideStore } from '@ngrx/store';
import { appReducers, effects } from 'landlord/+state';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { ConstantsLoaderService, CoreModule } from 'landlord/core';
import { ServiceWorkerModule } from '@angular/service-worker';
import { TranslateModule } from '@ngx-translate/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { provideRouterStore } from '@ngrx/router-store';
import { AuthModule } from 'landlord/auth/auth.module';
import { AccountModule } from 'landlord/screens/account/account.module';
import { MainModule } from 'landlord/main/main.module';
import { SearchModule } from 'landlord/search';
import { AppRouterModule } from 'landlord/app.router';
import { languageConfig } from 'landlord/config';
import { provideNgxMask } from 'ngx-mask';
import { storageKeys } from '@ui/landlord/shared/config';
import { provideHttpClient } from '@angular/common/http';
import { ChatWidgetService } from 'landlord/main/services/chat-widget.service';

if (environment.production) {
  enableProdMode();
}

const infrastructureConfig = {
  environment,
  apolloCacheIds: {
    MessageSource: 'messageKey'
  },
  storageKeys,
  languages: languageConfig
};

const sharedComponentsModuleConfig: ComponentsModuleConfig = {
  googleApiConfig: {
    apiKey: environment.google_api_key
  }
};

// to make the service tree shakable and not include it in other envs,
// we specify the envs that we want to provide the service on
function chatWidgetServiceFactory() {
  return ['production', 'sandbox', 'integration'].includes(
    environment.deploymentEnv
  )
    ? new ChatWidgetService()
    : null;
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      ServiceWorkerModule.register('/ngsw-worker.js', {
        enabled:
          environment.deploymentEnv === 'production' ||
          environment.deploymentEnv === 'sandbox'
      }),
      TranslateModule.forRoot(),
      BrowserModule,
      BrowserAnimationsModule,
      CommonModule,
      SharedComponentsModule.forRoot(sharedComponentsModuleConfig),
      PipesModule,
      FormsModule,
      ReactiveFormsModule.withConfig({
        callSetDisabledState: 'whenDisabledForLegacyCode'
      }),
      AngularSvgIconModule.forRoot(),
      NgbModule,
      InfrastructureModule.forRoot(infrastructureConfig),
      PortalsModule.forRoot(environment.backend_url),
      ProductsModule.forRoot(),
      PropertiesModule.forRoot(),
      AngularSvgIconModule.forRoot(),
      CoreModule,
      ThemeModule.forRoot({
        themes: [ImmomioTheme],
        active:
          (environment as EnvironmentConfig)?.app_design || ThemeUrls.IMMOMIO
      }),
      /**
       * App features modules.
       * Contain own routes, which are being added eagerly.
       */
      AuthModule,
      AccountModule,
      MainModule,
      SearchModule,
      /**
       * root router module
       * order is important here! AppRouterModule needs to be declared after any
       * eagerly loaded module, otherwise Router will start to resolve routes before
       * eagerly loaded routes are available.
       */
      AppRouterModule,
      KeycloakAuthenticationModule.forRoot({
        deepLinksConfig: {
          deepLinkingScheme:
            (environment as EnvironmentConfig)?.deepLinkingScheme ||
            ThemeUrls.IMMOMIO
        },
        keycloakConfig: {
          jsonConfig: () => environment?.keycloakConfig
        }
      })
    ),
    provideHttpClient(),
    ...guards,
    DateTimeService,
    BodyService,
    AdblockDetectorService,
    NewRelicService,
    provideStore(appReducers),
    provideEffects(effects),
    !environment.production
      ? provideStoreDevtools({ connectInZone: true })
      : [],
    {
      provide: ChatWidgetService,
      useFactory: chatWidgetServiceFactory
    },
    provideRouterStore({
      serializer: CustomSerializer
    }),
    { provide: CONSTANTS_LOADER, useClass: ConstantsLoaderService },
    provideNgxMask()
  ]
}).catch(err => console.error(err));
